import EventService from './event-service';
import _ from 'lodash';
import TransferManager from './transfer-manager';
import {
	initSession,
	registerStatusCallback,
	registerActivityCallback,
	startTransfer as sdkStartTransfer,
	getStatus as sdkGetStatus,
	getAllTransfers as sdkGetAllTransfers,
	showSelectFileDialog as sdkShowSelectFileDialog,
	showSelectFolderDialog as sdkShowSelectFolderDialog,
	resumeTransfer as sdkResumeTransfer,
	removeTransfer as sdkRemoveTransfer,
	showDirectory as sdkShowDirectory,
	stopTransfer as sdkStopTransfer,
	showTransferManager as sdkShowTransferManager,
	createDropzone as sdkCreateDropzone
} from '@ibm-aspera/sdk';

const CONNECT_UNAVAILABLE_STATUSES = new Set([null, 'FAILED', 'STOPPED']);

const TRANSFER_STATUS = {
	CANCELLED: 'cancelled',
	COMPLETED: 'completed',
	FAILED: 'failed',
	INITIATING: 'initiating',
	QUEUED: 'queued',
	REMOVED: 'removed',
	RUNNING: 'running',
	WILLRETRY: 'willretry'
};

const Aspera = {
	TRANSFER_STATUS,

	Connect: {
		startTransfer: (specs, options) => sdkStartTransfer(specs, options),
		removeTransfer: (uuid) => sdkRemoveTransfer(uuid),
		getAllTransfers: () => sdkGetAllTransfers().then((result) => ({
			transfers: Array.isArray(result) ? result : (result?.transfers ?? [])
		})),
		addEventListener: (event, cb) => {
			if(event === 'transfer') {
				EventService.on('Connect:Plugin:Transfer', (_eventType, data) => cb(event, data));
			}
		}
	},

	getStatus: () => {
		try {
			return sdkGetStatus();
		}
		catch{
			return null;
		}
	},

	startTransfer: (transferSpec, asperaSpec) => sdkStartTransfer(transferSpec, asperaSpec),

	showSelectFileDialog: (options) => sdkShowSelectFileDialog(options),

	showSelectFolderDialog: (options) => sdkShowSelectFolderDialog(options),

	createDropzone: (callback, elementId) => sdkCreateDropzone(callback, elementId),

	check: () => {
		const status = Aspera.getStatus();
		if(status !== 'RUNNING') {
			if(CONNECT_UNAVAILABLE_STATUSES.has(status)) {
				EventService.broadcast('Connect:NotInstalled');
			}
			return false;
		}
		return true;
	},

	isDisabled: false,

	init: () => {
		try {
			registerStatusCallback((status) => {
				EventService.broadcast('Connect:Plugin:Status', 'status', status);
			});

			registerActivityCallback((data) => {
				TransferManager.eventHandler(data);
				EventService.broadcast('Connect:Plugin:Transfer', 'transfer', data);
			});

			if(!window.Cypress) {
				initSession({
					appId: 'pixwel',
					connectSettings: {
						fallback: true,
						minVersion: '3.6.0',
						dragDropEnabled: true
					}
				});
			}
		}
		catch{
			EventService.broadcast('Connect:NotInstalled');
		}

		const addEvent = (event, cb) => {
			return EventService.on('Connect:' + event, (...args) => {
				return Aspera.check() ? cb(...args) : null;
			});
		};

		const selectFilesFolders = (type, e, options) => {
			return EventService.emit('Connect:Select' + type, {
				success: (files) => {
					if(!files.dataTransfer.files.length) {
						return;
					}
					const specs = {};
					specs[options.destination.nodeId] = {
						info: options.node,
						rootId: options.destination.fileId,
						files: files.dataTransfer.files.map((file) => {
							return { source: file.name };
						}),
						success: options.success
					};
					return EventService.emit('Connect:Upload', specs);
				}
			}, {
				title: 'Select ' + type
			});
		};

		const transferWithSpecs = (direction, specs) => {
			if(!Aspera.check()) {
				return;
			}

			for(let nodeId in specs) {
				const spec = specs[nodeId];

				const Spec = {
					direction,
					remote_host: spec.info?.host,
					paths: (spec.files ?? []).map((f) => ({ source: f.source }))
				};

				if(direction === 'receive' && spec.name) {
					Spec.paths = Spec.paths.map((path) => ({ ...path, destination: spec.name }));
				}

				if(!(Spec.direction && Spec.paths.length && Spec.remote_host)) {
					return;
				}

				EventService.emit('Connect:TransferStarted',
					Aspera.startTransfer(Spec, {})
						.then((data) => {
							if(typeof spec.success === 'function') {
								return spec.success(data);
							}
						})
						.catch((error) => {
							if(typeof spec.error === 'function') {
								spec.error(error);
							}
							throw error;
						}));
			}
		};

		_.forEach({
			'SelectFilesAndUpload': (e, options) => {
				selectFilesFolders('Files', e, options);
			},
			'SelectFoldersAndUpload': (e, options) => {
				selectFilesFolders('Folders', e, options);
			},
			'SelectFiles': (e, cb, options) => {
				const onSuccess = typeof cb === 'function' ? cb : cb?.success;
				const onError = cb?.error ?? _.noop;
				sdkShowSelectFileDialog(options).then(onSuccess)
					.catch(onError);
			},
			'SelectFolders': (e, cb, options) => {
				const onSuccess = typeof cb === 'function' ? cb : cb?.success;
				const onError = cb?.error ?? _.noop;
				sdkShowSelectFolderDialog(options).then(onSuccess)
					.catch(onError);
			},
			'Download': (e, specs) => {
				transferWithSpecs('receive', specs);
			},
			'Upload': (e, specs) => {
				transferWithSpecs('send', specs);
			},
			'Plugin:ResumeTransfer': (e, uuid) => {
				sdkResumeTransfer(uuid);
			},
			'Plugin:RemoveTransfer': (e, uuid) => {
				sdkRemoveTransfer(uuid);
			},
			'Plugin:ShowDirectory': (e, uuid) => {
				sdkShowDirectory(uuid);
			},
			'Plugin:StopTransfer': (e, uuid) => {
				sdkStopTransfer(uuid);
			},
			'Plugin:ShowTransferWindow': () => {
				sdkShowTransferManager();
			}
		}, (fn, name) => {
			addEvent(name, fn);
		});
	}
};

export default Aspera;
